import {
  Bundle,
  BundleId,
  BundleLine,
  BundleLineId,
  BundleLineVariant,
  BundleLineVariantId,
  Category,
  Product,
  ProductId,
  Quantity,
  Venue,
  getPrice,
  unwrapPrice,
  sumAdditionalPrice,
  SpecialPricePeriod,
} from "@greeter/core";
import { IGuestApi } from "@greeter/api";
import { assertEvent, assign, enqueueActions, raise, setup } from "xstate";
import {
  createFetchMachine,
  FetchActor,
  unwrapData,
} from "@greeter/xstate-util";
import {
  ApplyFn,
  change,
  ChangeState,
  createChangeState,
} from "@greeter/changes";
import { assert } from "@greeter/assert";
import { indexBy, indexById, upsert } from "@greeter/map";
import { CartMachineRef } from "../../Cart/CartMachine";
import { v7 } from "uuid";
import {
  indexBundles,
  indexBundleLines,
  indexBundleLineVariants,
  indexBundleLinesByVariantLines,
} from "./indexing";
import { BundleForm, BundleFormSchema, BundleLineForm } from "../../models";
import { Log } from "@greeter/log";
import { bundleLineLimitReached } from "./util";
import { DatePeriod } from "@greeter/date";
import { endOfMonth, startOfMonth } from "date-fns";

export function sumExtraPrice(bundle: BundleForm): number {
  let total = 0;
  for (const line of bundle.lines) {
    for (const extra of line.extra) {
      const v = line.variants.find((v) => v.id === extra.bundleLineVariantId);
      if (!v) continue;
      total += extra.quantity * v.salesPrice;
    }
  }
  return total;
}

export type PickedBundle = {
  cartLineId: string;
  bundleId: string;
  lines: Map<BundleLineId, Map<BundleLineVariantId, Quantity>>;
};

export interface BundlePickerApiRequirements
  extends Pick<
    IGuestApi,
    | "fetchBundlesByVenue"
    | "fetchVenue"
    | "fetchProducts"
    | "fetchCategories"
    | "fetchVenueFeatureFlag"
    | "fetchSpecialPricePeriods"
    | "fetchTicketEvents"
  > {}

export type PickingFor = {
  variantId?: BundleLineVariantId;
  bundleLineId?: BundleLineId;
};

function assertHasOne(pickingFor: PickingFor) {
  assert(
    pickingFor.variantId || pickingFor.bundleLineId,
    "either pickingFor.variantId or pickingFor.bundleLineId is required"
  );
}

type BundleIndexes = {
  /**
   * Indexes for quick lookup of data.
   */
  bundles: Map<BundleId, Bundle>;
  bundleLines: Map<BundleLineId, BundleLine>;
  bundleLineVariants: Map<BundleLineVariantId, BundleLineVariant>;
  bundleLinesByVariants: Map<BundleLineVariantId, BundleLine>;
};

type ProductIndexes = {
  // Lookup table for fetching image for a variant line.
  variantProducts: Map<ProductId, Product>;
};

type CategoryIndexes = {
  categories: Map<string, Category>;
};

export type BundlePickerContext = {
  venueId: string;
  venue?: Venue;

  _applier: ApplyFn<BundleForm, BundleChanges>;

  bundleId?: BundleId;

  forDate?: Date;

  /**
   * How many of this change bundle do we want?
   */
  quantity: number;
  bundleChanges?: ChangeState<BundleForm, BundleChanges>;
  bundlePrice: number;

  pickingFor?: PickingFor;
  prevPickingFor?: PickingFor;

  cartRef: CartMachineRef;

  upsellFeatureFlagRef: FetchActor<
    BundlePickerApiRequirements["fetchVenueFeatureFlag"]
  >;
  bundlesRef: FetchActor<BundlePickerApiRequirements["fetchBundlesByVenue"]>;
  productsRef: FetchActor<BundlePickerApiRequirements["fetchProducts"]>;
  categoriesRef: FetchActor<BundlePickerApiRequirements["fetchCategories"]>;

  ticketEventsRef: FetchActor<BundlePickerApiRequirements["fetchTicketEvents"]>;
  ticketEventsBundles: Map<BundleId, Bundle>;

  specialPricePeriods: Map<BundleId, SpecialPricePeriod[]>;
  specialPricePeriodsRef: FetchActor<
    BundlePickerApiRequirements["fetchSpecialPricePeriods"]
  >;
} & BundleIndexes &
  ProductIndexes &
  CategoryIndexes;

export type BundleChanges =
  | {
      type: "change";
      data: { bundleLine: BundleLineForm };
    }
  | {
      type: "setQuantity";
      data: {
        quantity: number;
        direction: "decrement" | "increment";
        variantId: string;
      };
    }
  | { type: "pick"; data: { variantId: string } };

function setupVariantChangeApplier(args: {
  indexBundleLineVariants: Map<BundleLineVariantId, BundleLineVariant>;
  pickingFor?: PickingFor;
}): ApplyFn<BundleForm, BundleChanges> {
  const { pickingFor } = args;

  return function apply(state, change) {
    Log.debug("[apply] applying", {
      state,
      change: { ...change },
      pickingFor: { ...pickingFor },
    });

    switch (change.type) {
      case "change": {
        state.lines = state.lines.map((l) =>
          l.id === change.data.bundleLine.id ? change.data.bundleLine : l
        );
        break;
      }

      case "pick": {
        if (!pickingFor) return;
        const bundleLine = pickingFor.bundleLineId
          ? state?.lines.find((l) => l.id === pickingFor.bundleLineId)
          : pickingFor.variantId
          ? state?.lines.find((l) =>
              l.variants.some((v) => v.id === pickingFor.variantId)
            )
          : undefined;

        if (!bundleLine) {
          Log.warn("Could not find the line for .pickingFor", {
            pickingFor,
            state,
          });
          return;
        }

        const variantToChangeWith = args.indexBundleLineVariants.get(
          change.data.variantId
        );

        if (!variantToChangeWith) {
          Log.warn("Could not find the variant to change with", {
            pickingFor,
            state,
            variantToChangeWith,
          });
          return;
        }

        const variant = bundleLine.variants.find(
          (v) =>
            v.id === variantToChangeWith.id && v.id !== pickingFor.variantId
        );

        // The existing variant that is in the line. If we find this
        // we should merge the pick into it
        const existingVariant = bundleLine.variants.find(
          (v) =>
            v.id === variantToChangeWith.id &&
            v.id !== pickingFor.variantId &&
            v.quantity &&
            v.quantity > 0
        );

        // The existing extra that is in the line. If we find this
        // we should merge the pick into it
        const existingExtra = bundleLine.extra.find(
          (v) =>
            v.bundleLineVariantId === change.data.variantId && v.quantity > 0
        );

        // Here we merge the overflowing extra on another
        const pickingForVariant = bundleLine.variants.find(
          (v) => v.id === pickingFor.variantId
        );
        const pickingForExtra = bundleLine.extra.find(
          (e) => e.bundleLineVariantId === pickingFor.variantId
        );

        const limitReached = bundleLineLimitReached(bundleLine);

        if (pickingForVariant) {
          if (existingVariant) {
            existingVariant.quantity ??= 0;
            existingVariant.quantity += pickingForVariant.quantity ?? 0;
          } else if (variant) {
            variant.quantity ??= 0;
            variant.quantity += pickingForVariant.quantity ?? 0;
          }

          pickingForVariant.quantity = 0;
        }

        if (pickingForExtra) {
          if (existingExtra) {
            existingExtra.quantity ??= 0;
            existingExtra.quantity += pickingForExtra.quantity ?? 0;
          } else {
            bundleLine.extra.push({
              bundleLineVariantId: change.data.variantId,
              bundleLineId: bundleLine.id,
              quantity: pickingForExtra.quantity,
            });
          }

          bundleLine.extra = bundleLine.extra.filter(
            (e) => e.bundleLineVariantId !== pickingForExtra.bundleLineVariantId
          );
        }

        if (!pickingFor.variantId && !limitReached && variant) {
          variant.quantity ??= 0;
          variant.quantity += 1;
        }

        if (!pickingFor.variantId && limitReached) {
          bundleLine.extra.push({
            bundleLineId: bundleLine.id,
            bundleLineVariantId: change.data.variantId,
            quantity: 1,
          });
        }
      }
    }
  };
}

export type BundlePickerModalEvents =
  | { type: "openCartSummary" }
  | { type: "closeCartSummary" }
  | { type: "openEditTables" }
  | { type: "closeEditTables" }
  | { type: "openAlmostThere" }
  | { type: "closeAlmostThere" }
  | { type: "openVariantLibrary"; data: PickingFor }
  | { type: "closeVariantLibrary" }
  | { type: "closeModals" };

export type BundlePickerApiEvents =
  | { type: "fetchVenue" }
  | { type: "fetchCategories" }
  | { type: "fetchProducts" }
  | { type: "fetchBundles" };

export type BundlePickerEvents =
  | { type: "back" }
  | { type: "cancel" }
  | { type: "next" }
  | { type: "prev" }
  | { type: "pickBundle"; data: BundleId }
  | { type: "pickStrategy" }
  | { type: "pickVariants"; data: BundleLineId }
  | { type: `pickManually` }
  | { type: "pickAutomatically" }
  | { type: "changeBundle"; data: BundleChanges }
  | { type: "setForDate"; data?: Date }
  | { type: "setQuantity"; data: { quantity: number } } // How many of this change bundle do we want?
  | {
      type: "internal.setBundleIndexes";
      data: {
        bundles: Map<BundleId, Bundle>;
        bundleLines: Map<BundleLineId, BundleLine>;
        bundleLineVariants: Map<BundleLineVariantId, BundleLineVariant>;
        bundleLinesByVariants: Map<BundleLineVariantId, BundleLine>;
      };
    }
  | { type: "internal.setProductIndexes"; data: ProductIndexes }
  | { type: "internal.setCategoryIndexes"; data: CategoryIndexes }
  | { type: "internal.setSpecialPricePeriods"; data: Array<SpecialPricePeriod> }
  | { type: "internal.refreshTicketEventBundles" }
  | { type: "finishPicking" }
  | { type: "validate" }
  | { type: "finish" }
  | BundlePickerApiEvents
  | BundlePickerModalEvents;

export type BundlePickerEmitted = {
  type: "finished";
  data: { pickedBundle: PickedBundle };
};

function resetChanges(bundle: BundleForm): Partial<BundlePickerContext> {
  return {
    quantity: 1,
    bundleChanges: createChangeState(bundle),
  };
}

function prepareFetchTicketEventsRequest(input: {
  venueId: string;
  forDate: Date;
}) {
  return {
    venueId: input.venueId,
    period: new DatePeriod({
      from: startOfMonth(input.forDate),
      to: endOfMonth(input.forDate),
    }),
  };
}

const bundleMachineSetup = setup({
  types: {} as {
    input: {
      venueId: string;
      api: BundlePickerApiRequirements;
      forDate?: Date;
      cartRef: BundlePickerContext["cartRef"];
    };
    emitted: {
      type: "bundlePicked";
      data: { id: BundleId; title?: string; changes: Bundle };
    };
    events: BundlePickerEvents;
    context: BundlePickerContext;
  },
  actions: {
    addBundleToCart: enqueueActions(({ context, enqueue }) => {
      assert(
        context.bundleChanges,
        "context.bundleChanges are required to add bundle to cart"
      );
      const cart = context.cartRef;
      const changedBundle = context.bundleChanges.value;
      cart.send({
        type: "change",
        data: {
          type: "add",
          data: {
            id: v7(),
            type: "bundle-form",
            form: changedBundle,
            quantity: context.quantity ?? 1,
          },
        },
      });

      enqueue.emit(({ context }) => ({
        type: "bundlePicked",
        data: {
          changes: context.bundleChanges?.value! ?? "",
          id: context.bundleChanges?.value.id ?? "",
          title: context.bundleChanges?.value.title ?? "",
        },
      }));

      assert(context.bundleId, "context.bundleId required to reset");
      const bundle =
        context.bundles.get(context.bundleId) ||
        context.ticketEventsBundles.get(context.bundleId);
      assert(bundle, "could not find the bundle", {
        bundleId: context.bundleId,
      });
      enqueue.assign(resetChanges(BundleFormSchema.parse(bundle)));
    }),
  },
});

export const bundlePickerMachine = bundleMachineSetup.createMachine({
  type: "parallel",
  context({ input, spawn, self }) {
    /**
     * NOTE/THOUGHT: spawning machines like this is still a bit of an experiment.
     * Do we like it over the verbose state solution?
     *
     * It could definitely simplify the overall design of the statemachine.
     */
    const specialPricePeriodsRef = spawn(
      createFetchMachine(input.api.fetchSpecialPricePeriods.bind(input.api))
    );
    specialPricePeriodsRef.on("fetched", ({ data }) => {
      self.send({ type: "internal.setSpecialPricePeriods", data });
    });

    const bundlesRef = spawn(
      createFetchMachine(input.api.fetchBundlesByVenue.bind(input.api))
    );
    bundlesRef.on("fetched", ({ data }) => {
      self.send({
        type: "internal.setBundleIndexes",
        data: {
          bundles: indexBundles(data),
          bundleLines: indexBundleLines(data),
          bundleLineVariants: indexBundleLineVariants(data),
          bundleLinesByVariants: indexBundleLinesByVariantLines(data),
        },
      });
      self.send({ type: "internal.refreshTicketEventBundles" });
      self.send({ type: "fetchProducts" });
      specialPricePeriodsRef.send({
        type: "fetch",
        data: [data.map((b) => b.id)],
      });
    });
    bundlesRef.on("error", () =>
      setTimeout(() => self.send({ type: "fetchBundles" }), 1_000)
    );
    bundlesRef.send({ type: "fetch", data: [input.venueId] });

    const ticketEventsRef = spawn(
      createFetchMachine(input.api.fetchTicketEvents.bind(input.api))
    );
    if (input.forDate) {
      ticketEventsRef.send({
        type: "fetch",
        data: [
          prepareFetchTicketEventsRequest({
            venueId: input.venueId,
            forDate: input.forDate,
          }),
        ],
      });
    }
    ticketEventsRef.on("fetched", () =>
      self.send({ type: "internal.refreshTicketEventBundles" })
    );

    const productsRef = spawn(
      createFetchMachine(input.api.fetchProducts.bind(input.api))
    );
    productsRef.on("error", () =>
      setTimeout(() => self.send({ type: "fetchProducts" }), 1_000)
    );
    productsRef.on("fetched", ({ data }) => {
      self.send({
        type: "internal.setProductIndexes",
        data: {
          variantProducts: indexBy(data, (p) => p.id),
        },
      });
    });

    const upsellFeatureFlagRef = spawn(
      createFetchMachine(input.api.fetchVenueFeatureFlag.bind(input.api))
    );

    const venueRef = spawn(
      createFetchMachine(input.api.fetchVenue.bind(input.api))
    );
    venueRef.on("error", () =>
      setTimeout(() => self.send({ type: "fetchVenue" }), 1_000)
    );
    venueRef.on("fetched", (ev) =>
      upsellFeatureFlagRef.send({
        type: "fetch",
        data: [{ venueId: ev.data.id, name: "upsell.v1.guest" }],
      })
    );
    venueRef.send({ type: "fetch", data: [input.venueId] });

    const categoriesRef = spawn(
      createFetchMachine(input.api.fetchCategories.bind(input.api))
    );
    categoriesRef.on("error", () =>
      setTimeout(() => self.send({ type: "fetchCategories" }), 1_000)
    );
    categoriesRef.on("fetched", ({ data }) => {
      self.send({
        type: "internal.setCategoryIndexes",
        data: {
          categories: indexBy(data, (p) => p.id),
        },
      });
    });

    return {
      venueId: input.venueId,
      bundleLinesByVariants: new Map(),
      _applier: setupVariantChangeApplier({
        indexBundleLineVariants: new Map(),
      }),
      categories: new Map(),
      bundles: new Map(),
      bundleLines: new Map(),
      bundleLineVariants: new Map(),
      variantProducts: new Map(),

      bundlePrice: 0,

      quantity: 1,

      cartRef: input.cartRef,

      ticketEventsRef,
      ticketEventsBundles: new Map(),

      upsellFeatureFlagRef,
      bundlesRef,
      productsRef,
      venueRef,
      categoriesRef,

      specialPricePeriods: new Map(),
      specialPricePeriodsRef,
    };
  },
  on: {
    "internal.refreshTicketEventBundles": {
      actions: assign(({ context }) => {
        if (!context.forDate) return {};

        Log.info("[internal.refreshTicketEventBundles]");

        const ticketEventsForDate = context.ticketEventsRef
          .getSnapshot()
          .context.data?.filter((te) => {
            if (te.eventPeriod.type === "once") {
              const datePeriod = DatePeriod.fromTimePeriod(
                new Date(te.eventPeriod.date),
                te.eventPeriod.timePeriod.from,
                te.eventPeriod.timePeriod.to
              );
              return context.forDate && datePeriod.isWithin(context.forDate);
            }
            return false;
          });

        Log.info("[internal.refreshTicketEventBundles]", {
          ticketEvents: context.ticketEventsRef.getSnapshot().context.data,
          ticketEventsForDate,
          d: context.forDate,
        });

        const bundleIds = new Set(
          ticketEventsForDate?.map((t) => t.bundles).flat()
        );
        const ticketEventBundles = Array.from(context.bundles.values()).filter(
          (b) => bundleIds.has(b.id)
        );

        const bundles = indexById(
          Array.from(context.bundles.values())?.filter(
            (b) => !bundleIds.has(b.id)
          ) ?? []
        );

        Log.info("[internal.refreshTicketEvnetBundles]", {
          bundles,
          bundleIds,
          ticketEventBundles,
        });

        return {
          bundles,
          ticketEventsBundles: indexById(ticketEventBundles),
        };
      }),
    },

    "internal.setBundleIndexes": {
      actions: assign(({ event }) => event.data),
    },

    "internal.setProductIndexes": {
      actions: assign({
        variantProducts: ({ event }) => event.data.variantProducts,
      }),
    },

    "internal.setCategoryIndexes": {
      actions: assign({
        categories: ({ event }) => event.data.categories,
      }),
    },

    "internal.setSpecialPricePeriods": {
      actions: [
        assign(({ event }) => {
          const index = new Map<BundleId, Array<SpecialPricePeriod>>();
          for (const s of event.data) {
            upsert(index, s.productId, s);
          }
          return {
            specialPricePeriods: index,
          };
        }),
        ({ context, event }) =>
          context.cartRef.send({
            type: "setSpecialPricePeriods",
            data: event.data,
          }),
      ],
    },

    setForDate: {
      actions: [
        assign({ forDate: unwrapData }),
        ({ context }) =>
          context.ticketEventsRef.send({
            type: "fetch",
            data: [
              prepareFetchTicketEventsRequest({
                venueId: context.venueId,
                forDate: context.forDate!,
              }),
            ],
          }),
        ({ context, event }) => context.cartRef.send(event),
      ],
    },

    fetchBundles: {
      actions: ({ context }) => {
        context.bundlesRef.send({
          type: "fetch",
          data: [context.venueId],
        });
      },
    },

    fetchProducts: {
      actions: ({ context }) => {
        const productIds = new Set(
          Array.from(context.bundleLineVariants.values()).map(
            (bv) => bv.productId
          )
        );
        context.productsRef.send({
          type: "fetch",
          data: [{ productIds: Array.from(productIds) }],
        });
      },
    },

    fetchCategories: {
      actions: ({ context }) => {
        if (!context.pickingFor) return [{ categoryIds: [] }];
        assertHasOne(context.pickingFor);

        const bundleLine = context.pickingFor.bundleLineId
          ? context.bundleLines.get(context.pickingFor.bundleLineId)
          : context.pickingFor.variantId
          ? context.bundleLinesByVariants.get(context.pickingFor.variantId)
          : undefined;

        assert(
          bundleLine,
          "could not find the bundleLine based on the pickingFor",
          { pickingFor: context.pickingFor }
        );

        const productIds = bundleLine.variants.map((v) => v.productId);
        const categoryIds =
          productIds
            ?.map((id) => {
              const product = context.variantProducts.get(id);
              return product?.categories;
            })
            .filter((id) => !!id)
            .flat() ?? [];
        return context.categoriesRef.send({
          type: "fetch",
          data: [{ categoryIds: categoryIds }],
        });
      },
    },

    setQuantity: {
      actions: assign(({ event }) => {
        return { quantity: Math.max(1, event.data.quantity) };
      }),
    },
  },
  states: {
    modal: {
      type: "parallel",
      states: {
        editTables: {
          initial: "closed",
          states: {
            open: {
              on: { closeEditTables: "closed", closeModals: "closed" },
            },
            closed: {
              on: { openEditTables: "open" },
            },
          },
        },

        almostThere: {
          initial: "closed",
          states: {
            open: {
              on: { closeAlmostThere: "closed", closeModals: "closed" },
            },
            closed: {
              on: { openAlmostThere: "open" },
            },
          },
        },

        variantLibrary: {
          initial: "closed",
          states: {
            open: {
              on: {
                closeVariantLibrary: "closed",
                closeModals: "closed",
              },
              exit: assign(({ context }) => ({
                pickingFor: undefined,
                prevPickingFor: context.pickingFor,
              })),
            },
            closed: {
              on: {
                openVariantLibrary: {
                  target: "open",
                  actions: assign(({ event, context }) => {
                    return {
                      pickingFor: event.data,
                      _applier: setupVariantChangeApplier({
                        indexBundleLineVariants: context.bundleLineVariants,
                        pickingFor: event.data,
                      }),
                    };
                  }),
                },
              },
            },
          },
        },
      },
    },
    ui: {
      initial: "pickingBundle",
      on: {
        validate: {
          actions: enqueueActions(({ enqueue, context }) => {
            assert(
              context.bundleId,
              `missing bundleId in order to emit "bundlePicked"`
            );
            assert(
              context.bundleChanges,
              "context.bundleChanges is required to emit bundlePicked"
            );
            enqueue.emit({
              type: "bundlePicked",
              data: {
                id: context.bundleId,
                title: context.bundles.get(context.bundleId)?.title ?? "",
                changes: context.bundleChanges?.value,
              },
            });
            enqueue.raise({ type: "finishPicking" });
          }),
        },
      },
      states: {
        pickingBundle: {
          on: {
            pickStrategy: "pickingStrategy",
            pickBundle: {
              actions: enqueueActions(({ context, event, enqueue }) => {
                assertEvent(event, "pickBundle");

                const bundle =
                  context.bundles.get(event.data) ||
                  context.ticketEventsBundles.get(event.data);
                if (!bundle) return;

                enqueue.assign({
                  bundleId: event.data,
                  bundleChanges: createChangeState(
                    BundleFormSchema.parse(bundle)
                  ),
                  bundlePrice:
                    unwrapPrice(
                      getPrice(bundle, {
                        forDate: context.forDate,
                        specialPricePeriods: context.specialPricePeriods.get(
                          bundle.id
                        ),
                      })
                    ) + sumAdditionalPrice(bundle),
                });
                if (bundle.lines.length === 0) {
                  enqueue.raise({ type: "validate" });
                } else {
                  enqueue.raise({ type: "pickStrategy" });
                }
              }),
            },
            next: {
              target: "pickingStrategy",
            },
          },
        },
        pickingStrategy: {
          on: {
            prev: "pickingBundle",
            next: "pickingVariants",
            pickAutomatically: {
              actions: "addBundleToCart",
            },
            pickManually: "pickingVariants",
          },
        },
        pickingVariants: {
          on: {
            changeBundle: {
              actions: assign(({ context, event }) => {
                assert(
                  context.bundleChanges,
                  "context.bundleChanges are required for creating a change"
                );
                const changes = change(
                  context.bundleChanges,
                  context._applier,
                  event.data
                );
                return {
                  bundleChanges: {
                    ...changes,
                    value: changes.value,
                  },
                  bundlePrice:
                    unwrapPrice(
                      getPrice(changes.value, {
                        forDate: context.forDate,
                        specialPricePeriods: context.specialPricePeriods.get(
                          changes.value.id
                        ),
                      })
                    ) +
                    sumAdditionalPrice(changes.value) +
                    sumExtraPrice(changes.value),
                };
              }),
            },
            cancel: {
              target: "pickingBundle",
              actions: assign(({ context }) => {
                assert(context.bundleId, "context.bundleId are required to ");
                const bundle = context.bundles.get(context.bundleId);
                assert(bundle, "could not find the bundle", {
                  bundleId: context.bundleId,
                });
                return resetChanges(BundleFormSchema.parse(bundle));
              }),
            },
            finishPicking: {
              target: "pickingBundle",
              actions: ["addBundleToCart", raise({ type: "closeModals" })],
            },
            prev: { target: "pickingStrategy" },
          },
        },
      },
    },
  },
});
